import { useRouter } from "@tanstack/react-router"
import { useCallback, useEffect, useRef, useState } from "react"

import { useAuthStore } from "@/stores/authStore"

interface UseNavigationConfirmationProps {
  shouldBlock: boolean
  allowedPath?: string
  onConfirmNavigation?: () => void
}

interface UseNavigationConfirmationReturn {
  isConfirmationOpen: boolean
  handleConfirmNavigation: () => void
  handleCancelNavigation: () => void
}

/**
 * Blocks navigation when there are unsaved changes. Works around TanStack Router v1.92.6 lacking native blocking.
 *
 * Uses three methods with trade-offs:
 * 1. Click interception - Perfect UX for link clicks (side menu, etc.)
 * 2. Route monitoring + back navigation - Catches URL paste/back button (brief flash visible)
 * 3. Browser beforeunload - Native dialog for page close/refresh (non-customizable)
 *
 * @param shouldBlock - Whether to block navigation and show confirmation
 * @param onConfirmNavigation - Callback to reset form before proceeding (prevents infinite loop)
 * @param allowedPath - Path prefix to allow navigation without confirmation (e.g., tab navigation)
 */
export const useNavigationConfirmation = ({
  shouldBlock,
  onConfirmNavigation,
  allowedPath,
}: UseNavigationConfirmationProps): UseNavigationConfirmationReturn => {
  const router = useRouter()
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null)
  const previousPathRef = useRef(router.state.location.pathname)
  const isConfirmingRef = useRef(false)
  const isNavigatingRef = useRef(false)
  const authStatus = useAuthStore((state) => state.status)

  const isNavigationAllowed = useCallback(
    (targetPath: string): boolean => {
      if (!allowedPath) {
        return false
      }

      return targetPath.startsWith(allowedPath)
    },
    [allowedPath]
  )

  // Link clicks (side menu, navigation links)
  useEffect(() => {
    if (!shouldBlock) {
      return
    }

    const handleClick = (event: MouseEvent) => {
      const target = (event.target as HTMLElement).closest("a[href]")

      if (!target) {
        return
      }

      const href = target.getAttribute("href")

      if (!href || href.startsWith("#") || href.startsWith("http")) {
        return
      }

      const currentPath = router.state.location.pathname

      if (href === currentPath) {
        return
      }

      if (isNavigationAllowed(href)) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      setPendingNavigation(href)
      setIsConfirmationOpen(true)
    }

    document.addEventListener("click", handleClick, true)

    return () => {
      document.removeEventListener("click", handleClick, true)
    }
  }, [shouldBlock, router.state.location.pathname, isNavigationAllowed])

  // Route changes (URL paste, back button, programmatic navigation)
  useEffect(() => {
    if (!shouldBlock) {
      return
    }

    const currentPath = router.state.location.pathname
    const hasPathChanged = currentPath !== previousPathRef.current
    const isConfirming = isConfirmingRef.current || isNavigatingRef.current

    if (!hasPathChanged || isConfirming) {
      previousPathRef.current = currentPath
      return
    }

    if (isNavigationAllowed(currentPath)) {
      previousPathRef.current = currentPath
      return
    }

    setIsConfirmationOpen(true)
    setPendingNavigation(currentPath)

    isNavigatingRef.current = true
    router.history.back()

    setTimeout(() => {
      isNavigatingRef.current = false
    }, 100)

    previousPathRef.current = currentPath
  }, [router.state.location.pathname, router.history, shouldBlock, isNavigationAllowed])

  // Browser refresh/tab close
  useEffect(() => {
    if (!shouldBlock || authStatus === "unauthenticated") {
      return
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      return "You have unsaved changes. Are you sure you want to leave?"
    }

    window.addEventListener("beforeunload", handleBeforeUnload)

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [shouldBlock, authStatus])

  const handleConfirmNavigation = () => {
    isConfirmingRef.current = true

    onConfirmNavigation?.()

    if (pendingNavigation) {
      router.navigate({ to: pendingNavigation })
    }

    setIsConfirmationOpen(false)
    setPendingNavigation(null)

    setTimeout(() => {
      isConfirmingRef.current = false
    }, 100)
  }

  const handleCancelNavigation = () => {
    setIsConfirmationOpen(false)
    setPendingNavigation(null)
  }

  return { isConfirmationOpen, handleConfirmNavigation, handleCancelNavigation }
}
